import { Sprite } from "pixi.js"
import { Coordinate, TileSize, WindLeavesConfig } from "game-common/models"
import { daylightFraction } from "./day_gate"
import { windAt } from "./wind"
import type { SurfaceObject } from "./tile_effect_system"

interface BentTree {
    object: SurfaceObject
    angle: number
    pieces: Array<{ sprite: Sprite; x: number; written: number; skew: number }>
}

/** Small, root-pinned bends on the same gust as grass and airborne leaves. No extra draw calls. */
export class TreeSway {
    private bent = new Map<string, BentTree>()
    private nearby: Array<[string, SurfaceObject]> = []
    private gatherAt = -Infinity
    private gatherX = Infinity
    private gatherY = Infinity
    private objectCount = -1

    constructor(private objects: Map<string, SurfaceObject>) {}

    // Restore before hits, falls, redraws, or the next frame. Ownership and last-write checks keep
    // detached/recycled pool sprites out of this effect's hands.
    release = (key: string): number => {
        const tree = this.bent.get(key)
        if (!tree) return 0
        if (this.objects.get(key) === tree.object) {
            for (const p of tree.pieces) {
                const s = p.sprite
                if (!s.destroyed && s.parent && tree.object.spriteSet.includes(s) &&
                    s.x === p.written && s.skew.x === p.skew) {
                    s.x = p.x
                    s.skew.x = 0
                }
            }
        }
        this.bent.delete(key)
        return tree.angle
    }

    clear = () => { for (const key of this.bent.keys()) this.release(key) }

    reset = () => {
        this.clear()
        this.nearby = []
        this.gatherAt = -Infinity
    }

    update = (config: WindLeavesConfig | null, center?: Coordinate, storm = 0, brightness?: number,
        busy: (key: string) => boolean = () => false, now = performance.now()) => {
        this.clear()
        if (!center || (window as any).treeSway === false) return
        const wind = windAt(config ?? (storm > 0 ? { enabled: true } : null), now / 1000)
        if (!wind) return
        const strength = 0.0025 * (0.3 + 0.7 * daylightFraction(brightness)) + 0.013 * storm
        // Gather occasionally rather than scanning the room's entire tile registry every frame.
        // The extra tile of margin covers movement until the next gather. Replaced objects are
        // rejected below, so a removed tree can never keep bending a reused pool sprite.
        if (now - this.gatherAt >= 250 || this.objectCount !== this.objects.size ||
            Math.abs(center.x - this.gatherX) > TileSize || Math.abs(center.y - this.gatherY) > TileSize) {
            this.nearby = []
            for (const entry of this.objects) {
                const root = entry[1].treeWind
                if (root && Math.abs(root.x - center.x) <= 13 * TileSize &&
                    Math.abs(root.y - center.y) <= 13 * TileSize) this.nearby.push(entry)
            }
            this.gatherAt = now
            this.gatherX = center.x; this.gatherY = center.y
            this.objectCount = this.objects.size
        }
        for (const [key, object] of this.nearby) {
            const root = object.treeWind!
            if (this.objects.get(key) !== object || busy(key)) continue
            const phase = (root.x * wind.x + root.y * wind.y) / (6 * TileSize) +
                Math.sin(root.x * 12.9898 + root.y * 78.233) * 0.45
            let angle = wind.x * wind.gust * strength * (0.65 + 0.35 * Math.sin(now / 1000 * 1.4 - phase))
            // A tall tree should still move just a few pixels at its crown.
            let top = Infinity
            for (const s of object.spriteSet) if (!s.destroyed && s.parent) top = Math.min(top, s.y)
            if (top === Infinity) continue
            const height = root.y - top
            const limit = 4 / Math.max(1, height)
            angle = Math.max(-limit, Math.min(limit, angle))
            // Never write an invalid weather value into a persistent sprite transform. NaN
            // also fails the last-write equality check in release(), so it cannot be restored.
            if (!Number.isFinite(angle)) continue
            const pieces: BentTree["pieces"] = []
            for (const sprite of object.spriteSet) {
                if (sprite.destroyed || !sprite.parent) continue
                const x = sprite.x
                const skew = -Math.asin(angle)
                sprite.x = x + angle * (root.y - sprite.y)
                sprite.skew.x = skew
                pieces.push({ sprite, x, written: sprite.x, skew })
            }
            if (pieces.length) this.bent.set(key, { object, angle, pieces })
        }
    }
}
